import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  X, 
  Plus, 
  ImageIcon, 
  BookOpen, 
  Tag,
  Loader2,
  PenTool
} from 'lucide-react';
import { toast } from 'sonner';

const CreateComic = () => {
  const [searchParams] = useSearchParams();
  const preselectedSeriesId = searchParams.get('series');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    images: [],
    series_id: preselectedSeriesId || '',
    series_name: '',
    episode_number: '',
    categories: [],
    tags: []
  });
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [series, setSeries] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch user's series
      const seriesResponse = await axios.get(`${API}/series?author_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeries(seriesResponse.data);
      
      // If preselected series, set the form data
      if (preselectedSeriesId) {
        const selectedSeries = seriesResponse.data.find(s => s.id === preselectedSeriesId);
        if (selectedSeries) {
          setFormData(prev => ({
            ...prev,
            series_name: selectedSeries.name,
            categories: [...selectedSeries.categories]
          }));
          
          // Get next episode number
          const comicsResponse = await axios.get(`${API}/comics?series_id=${preselectedSeriesId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const maxEpisode = Math.max(...comicsResponse.data.map(c => c.episode_number || 0), 0);
          setFormData(prev => ({
            ...prev,
            episode_number: (maxEpisode + 1).toString()
          }));
        }
      }
      
      // Fetch available categories
      const categoriesResponse = await axios.get(`${API}/categories`);
      setAvailableCategories(categoriesResponse.data.categories || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load series data');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSeriesChange = (value) => {
    const selectedSeries = series.find(s => s.id === value);
    setFormData(prev => ({
      ...prev,
      series_id: value === "none" ? "" : value,
      series_name: selectedSeries ? selectedSeries.name : '',
      categories: selectedSeries ? [...selectedSeries.categories] : []
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, event.target.result]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const removeCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.images.length === 0) {
      setError('Please add at least one image');
      return;
    }
    
    if (formData.series_id && !formData.episode_number) {
      setError('Please specify episode number for series comics');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        episode_number: formData.episode_number ? parseInt(formData.episode_number) : null
      };
      
      await axios.post(`${API}/comics`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Comic created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating comic:', error);
      setError(error.response?.data?.detail || 'Failed to create comic. Please try again.');
      toast.error('Failed to create comic');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in-up">
            <h1 className="text-4xl font-bold gradient-text mb-4 flex items-center justify-center">
              <PenTool className="w-8 h-8 mr-3" />
              Create New Comic
            </h1>
            <p className="text-gray-600">Share your comic story with the Comica community</p>
          </div>

          <Card className="glass-card animate-slide-in-right">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ImageIcon className="w-5 h-5" />
                <span>Comic Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Title & Description */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="title">Comic Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter your comic title"
                      className="form-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="series_id">Series (Optional)</Label>
                    <Select value={formData.series_id || "none"} onValueChange={handleSeriesChange}>
                      <SelectTrigger className="form-input">
                        <SelectValue placeholder="Select a series" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Series</SelectItem>
                        {series.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.series_id && (
                  <div className="space-y-2">
                    <Label htmlFor="episode_number">Episode Number</Label>
                    <Input
                      id="episode_number"
                      name="episode_number"
                      type="number"
                      min="1"
                      value={formData.episode_number}
                      onChange={handleChange}
                      placeholder="Episode number"
                      className="form-input max-w-xs"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your comic..."
                    className="form-input resize-none"
                    rows={4}
                  />
                </div>

                {/* Image Upload */}
                <div className="space-y-4">
                  <Label>Comic Images *</Label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center space-y-2"
                    >
                      <Upload className="w-10 h-10 text-gray-400" />
                      <span className="text-gray-600">Click to upload images</span>
                      <span className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB each</span>
                    </label>
                  </div>

                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Comic page ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Categories */}
                <div className="space-y-4">
                  <Label>Categories</Label>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.categories.map((category) => (
                      <Badge key={category} variant="secondary" className="category-tag">
                        {category}
                        <button
                          type="button"
                          onClick={() => removeCategory(category)}
                          className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Input
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Add category"
                      className="form-input flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                    />
                    <Button type="button" onClick={addCategory} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {availableCategories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm text-gray-500">Popular:</span>
                      {availableCategories.slice(0, 5).map((category) => (
                        <Button
                          key={category}
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => !formData.categories.includes(category) && 
                            setFormData(prev => ({ ...prev, categories: [...prev.categories, category] }))}
                          className="text-xs h-6 px-2"
                          disabled={formData.categories.includes(category)}
                        >
                          {category}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="space-y-4">
                  <Label>Tags (Optional)</Label>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag"
                      className="form-input flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" onClick={addTag} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <Button
                    type="submit"
                    disabled={loading || formData.images.length === 0}
                    className="flex-1 btn-primary"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Comic...
                      </>
                    ) : (
                      <>
                        <PenTool className="w-4 h-4 mr-2" />
                        Publish Comic
                      </>
                    )}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="bg-white/50"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateComic;